import type { FilterRule, FilterField, FilterMatch, FilterOperator } from '@b4l/shared'
import { Button } from '../ui/Button'

const FIELDS: { value: FilterField; label: string }[] = [
  { value: 'from', label: 'Expeditor' },
  { value: 'to', label: 'Destinatar' },
  { value: 'subject', label: 'Subiect' },
  { value: 'body', label: 'Conținut' },
]

const MATCHES: { value: FilterMatch; label: string }[] = [
  { value: 'contains', label: 'conține' },
  { value: 'exact', label: 'egal' },
  { value: 'domain', label: 'domeniu (@x.ro)' },
  { value: 'regex', label: 'regex' },
]

interface BuilderProps {
  rule: FilterRule
  onChange: (rule: FilterRule) => void
  /** Hide controls for switching to/from group at the root level. */
  isRoot?: boolean
  onRemove?: () => void
}

export function FilterRuleBuilder({ rule, onChange, isRoot, onRemove }: BuilderProps) {
  const isGroup = 'operator' in rule

  return (
    <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
      {isGroup ? (
        <GroupEditor
          rule={rule}
          onChange={onChange}
          isRoot={isRoot}
          onRemove={onRemove}
          onConvertToLeaf={() => onChange({ field: 'subject', match: 'contains', value: '' })}
        />
      ) : (
        <LeafEditor
          rule={rule}
          onChange={onChange}
          onRemove={onRemove}
          onConvertToGroup={(operator) =>
            onChange({
              operator,
              rules: [rule, { field: 'subject', match: 'contains', value: '' }],
            })
          }
        />
      )}
    </div>
  )
}

function GroupEditor({
  rule,
  onChange,
  isRoot,
  onRemove,
  onConvertToLeaf,
}: {
  rule: Extract<FilterRule, { operator: FilterOperator }>
  onChange: (rule: FilterRule) => void
  isRoot?: boolean
  onRemove?: () => void
  onConvertToLeaf: () => void
}) {
  const updateChild = (idx: number, child: FilterRule) => {
    const next = [...rule.rules]
    next[idx] = child
    onChange({ ...rule, rules: next })
  }

  const removeChild = (idx: number) => {
    if (rule.rules.length <= 1) return
    onChange({ ...rule, rules: rule.rules.filter((_, i) => i !== idx) })
  }

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <select
          value={rule.operator}
          onChange={(e) => onChange({ ...rule, operator: e.target.value as FilterOperator })}
          className="rounded border border-b4l-border bg-white px-2 py-1 text-xs font-semibold"
        >
          <option value="AND">Toate condițiile (ȘI)</option>
          <option value="OR">Cel puțin una (SAU)</option>
        </select>
        {!isRoot && rule.rules.length === 1 && (
          <Button
            variant="ghost"
            onClick={onConvertToLeaf}
            className="!text-[10px] !text-b4l-text-muted"
          >
            Transformă în regulă simplă
          </Button>
        )}
        {!isRoot && onRemove && (
          <Button
            variant="danger"
            onClick={onRemove}
            className="!ml-auto !px-2 !py-0.5 !text-[10px]"
          >
            Șterge grupul
          </Button>
        )}
      </div>
      <div className="space-y-2 pl-2">
        {rule.rules.map((child, idx) => (
          <FilterRuleBuilder
            key={idx}
            rule={child}
            onChange={(c) => updateChild(idx, c)}
            onRemove={rule.rules.length > 1 ? () => removeChild(idx) : undefined}
          />
        ))}
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              onChange({
                ...rule,
                rules: [...rule.rules, { field: 'subject', match: 'contains', value: '' }],
              })
            }
            className="!px-2 !py-1 !text-xs"
          >
            + Condiție
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              onChange({
                ...rule,
                rules: [
                  ...rule.rules,
                  {
                    operator: rule.operator === 'AND' ? 'OR' : 'AND',
                    rules: [{ field: 'subject', match: 'contains', value: '' }],
                  },
                ],
              })
            }
            className="!px-2 !py-1 !text-xs"
          >
            + Subgrup
          </Button>
        </div>
      </div>
    </div>
  )
}

function LeafEditor({
  rule,
  onChange,
  onRemove,
  onConvertToGroup,
}: {
  rule: Extract<FilterRule, { field: FilterField }>
  onChange: (rule: FilterRule) => void
  onRemove?: () => void
  onConvertToGroup: (op: FilterOperator) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={rule.field}
        onChange={(e) => onChange({ ...rule, field: e.target.value as FilterField })}
        className="rounded border border-b4l-border bg-white px-2 py-1 text-xs"
      >
        {FIELDS.map((f) => (
          <option key={f.value} value={f.value}>
            {f.label}
          </option>
        ))}
      </select>
      <select
        value={rule.match}
        onChange={(e) => onChange({ ...rule, match: e.target.value as FilterMatch })}
        className="rounded border border-b4l-border bg-white px-2 py-1 text-xs"
      >
        {MATCHES.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={rule.value}
        onChange={(e) => onChange({ ...rule, value: e.target.value })}
        placeholder={
          rule.match === 'domain'
            ? '@dr-max.ro'
            : rule.match === 'regex'
              ? 'B4L-PRINT-\\d{6}'
              : 'text...'
        }
        className="min-w-[200px] flex-1 rounded border border-b4l-border bg-white px-2 py-1 text-xs"
      />
      <Button
        variant="ghost"
        onClick={() => onConvertToGroup('AND')}
        className="!px-2 !py-0.5 !text-[10px] !text-b4l-text-muted"
      >
        + ȘI
      </Button>
      <Button
        variant="ghost"
        onClick={() => onConvertToGroup('OR')}
        className="!px-2 !py-0.5 !text-[10px] !text-b4l-text-muted"
      >
        + SAU
      </Button>
      {onRemove && (
        <Button variant="danger" onClick={onRemove} className="!px-2 !py-0.5 !text-[10px]">
          ×
        </Button>
      )}
    </div>
  )
}
